import { ChevronDown } from '@untitledui/icons'
import { isValidElement } from 'react'
import {
  Button as AriaButton,
  ListBox as AriaListBox,
  Select as AriaSelect,
  SelectValue as AriaSelectValue
} from 'react-aria-components'
import { twMerge } from 'tailwind-merge'

import { Avatar } from '@/lib/components/designSystem/Avatar'
import { HintText } from '@components/designSystem/input/hint-text'
import { Label } from '@components/designSystem/input/label'
import { SelectContext as SelectContext1 } from '@components/designSystem/select/context'
import { sizes } from '@components/designSystem/select/sizes'
import { cx } from '@utils/cx.ts'
import { isReactComponent } from '@utils/is-react-component.ts'

import { ComboBox } from './combobox'
import { Popover } from './popover'
import { SelectItem } from './select-item'

import type { FC, ReactNode, Ref, RefAttributes } from 'react'
import type { SelectProps as AriaSelectProps } from 'react-aria-components'

export interface SelectItemType {
    id: string
    label?: string
    avatarUrl?: string
    isDisabled?: boolean
    supportingText?: string
    icon?: FC | ReactNode
}

export interface CommonProps {
    hint?: string
    label?: string
    tooltip?: string
    size?: 'sm' | 'md'
    placeholder?: string
}

interface SelectProps extends Omit<AriaSelectProps<SelectItemType>, 'children' | 'items'>, RefAttributes<HTMLDivElement>, CommonProps {
    items?: SelectItemType[]
    popoverClassName?: string
    placeholderIcon?: FC | ReactNode
    children: ReactNode | ((item: SelectItemType) => ReactNode)
}

interface SelectValueProps {
    isOpen: boolean
    size: 'sm' | 'md'
    isFocused: boolean
    isDisabled: boolean
    placeholder?: string
    ref?: Ref<HTMLButtonElement>
    placeholderIcon?: FC | ReactNode
}

const styles = {
  label: {
    sm: 'text-sm',
    md: 'text-md',
  },
  supportingText: {
    sm: 'text-sm',
    md: 'text-md',
  },
}

const SelectValue = ({ isOpen, isFocused, isDisabled, size, placeholder, placeholderIcon, ref }: SelectValueProps) => {
  return (
    <AriaButton
      ref={ref}
      className={cx(
        `
          relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-border-secondary
          outline-hidden transition duration-100 ease-linear ring-inset
        `,
        (isFocused || isOpen) && 'ring-2 ring-brand',
        isDisabled && 'cursor-not-allowed bg-disabled_subtle text-disabled'
      )}
    >
      <AriaSelectValue<SelectItemType>
        className={cx(
          'flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle',

          // Icon styles
          `
            *:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary
            in-disabled:*:data-icon:text-fg-disabled
          `,

          sizes[size].root
        )}
      >
        {(state) => {
          const selectedItem = state.state.selectedItems[0]?.value
          const Icon = selectedItem?.icon ?? placeholderIcon
          return (
            <>
              {selectedItem?.avatarUrl ? (
                <Avatar size="xs" src={selectedItem.avatarUrl} alt={selectedItem.label} />
              ) : isReactComponent(Icon) ? (
                <Icon data-icon aria-hidden="true" />
              ) : isValidElement(Icon) ? (
                Icon
              ) : null}

              {selectedItem ? (
                <section className="flex w-full gap-2 truncate">
                  <p className={twMerge('truncate font-medium text-primary', styles.label[size])}>{selectedItem.label}</p>
                  {selectedItem.supportingText && <p className={twMerge('text-tertiary', styles.supportingText[size])}>{selectedItem.supportingText}</p>}
                </section>
              ) : (
                <p className={cx('text-md text-placeholder', isDisabled && 'text-disabled')}>{placeholder}</p>
              )}

              <ChevronDown
                aria-hidden="true"
                className={cx('ml-auto shrink-0 text-fg-quaternary', size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5')}
              />
            </>
          )
        }}
      </AriaSelectValue>
    </AriaButton>
  )
}

const Select = ({ placeholder = 'Select', placeholderIcon, size = 'sm', children, items, label, hint, tooltip, className, ...rest }: SelectProps) => {
  return (
    <SelectContext1 value={{ size }}>
      <AriaSelect {...rest} className={(state) => cx('flex flex-col gap-1.5', typeof className === 'function' ? className(state) : className)}>
        {(state) =>{
          return (
            <>
              {label && (
                <Label isRequired={state.isRequired} tooltip={tooltip}>
                  {label}
                </Label>
              )}

              <SelectValue {...state} {...{ size, placeholder }} placeholderIcon={placeholderIcon} />

              <Popover size={size} className={rest.popoverClassName}>
                <AriaListBox items={items} className="size-full outline-hidden">
                  {children}
                </AriaListBox>
              </Popover>

              {hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
            </>
          )
        }}
      </AriaSelect>
    </SelectContext1>
  )
}

const _Select = Select as typeof Select & {
    ComboBox: typeof ComboBox
    Item: typeof SelectItem
}
_Select.ComboBox = ComboBox
_Select.Item = SelectItem

export { _Select as Select }
